import { ChevronDown } from 'lucide-react';
import { forwardRef } from 'react';
import type * as React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

/**
 * Campo encorpado do painel — substitui o `<Input>` cru do shadcn nas telas do admin.
 *
 * Geometria (não altere, é o que dá a "cara" do sistema):
 *   raio 14px · borda 1px border-border · fundo bg-field · padding 13px 18px
 *   rótulo 10px/700/tracking .14em em caixa alta, DENTRO do campo, acima do valor
 *   valor 16px/600 em text-white · altura resultante ≈ 66px
 *   foco: borda olive-line, fundo bg-accent e trilho oliva de 3px acendendo à esquerda
 *
 * O trilho e o foco são :focus-within puro — sem estado em JS.
 */
type FieldProps = React.ComponentProps<'input'> & {
  label: string;
  required?: boolean;
  /** Elemento à direita do valor: ícone de calendário, botão "Copiar". */
  trailing?: React.ReactNode;
  /** Elemento à esquerda do valor: use <MoneyPrefix /> para R$. */
  leading?: React.ReactNode;
  /** Aplica a fonte display no valor (usar em valores monetários). */
  displayValue?: boolean;
  fieldClassName?: string;
  invalid?: boolean;
};

export const Field = forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      label,
      required,
      trailing,
      leading,
      displayValue,
      fieldClassName,
      className,
      invalid,
      ...props
    },
    ref,
  ) => (
    <label
      className={cn(
        'group relative flex h-[66px] min-w-0 cursor-text flex-col justify-center gap-1.5 rounded-[14px] border border-border bg-field px-[18px] py-[13px]',
        'transition-[background-color,border-color] duration-150',
        'focus-within:border-olive-line focus-within:bg-accent',
        invalid && 'border-destructive',
        fieldClassName,
      )}
    >
      <span className="pointer-events-none absolute inset-y-[13px] left-0 w-[3px] rounded-full bg-olive-icon opacity-0 transition-opacity duration-150 group-focus-within:opacity-100" />
      <span className="col-label text-muted-foreground">
        {label} {required && <span className="text-sand">*</span>}
      </span>
      <span className="flex items-center gap-2.5">
        {leading}
        <input
          {...props}
          ref={ref}
          className={cn(
            'min-w-0 flex-1 border-0 bg-transparent p-0 text-base font-semibold text-white outline-none placeholder:text-faint',
            displayValue && 'font-display text-lg font-extrabold tracking-tight',
            className,
          )}
        />
        {trailing}
      </span>
    </label>
  ),
);
Field.displayName = 'Field';

/** Chip "R$" que antecede o valor monetário. */
export const MoneyPrefix: React.FC = () => (
  <span className="font-display shrink-0 rounded-[7px] border border-olive-line/40 bg-accent px-2.5 py-[3px] text-xs font-extrabold text-olive-ink">
    R$
  </span>
);

type TextFieldProps = React.ComponentProps<'textarea'> & {
  label: string;
  /** Contador "96 / 180" à direita do rótulo. */
  counter?: string;
  fieldClassName?: string;
};

export const TextField = forwardRef<HTMLTextAreaElement, TextFieldProps>(
  ({ label, counter, fieldClassName, className, ...props }, ref) => (
    <label
      className={cn(
        'group relative flex min-w-0 cursor-text flex-col gap-[7px] rounded-[14px] border border-border bg-field px-[18px] py-[13px]',
        'transition-[background-color,border-color] duration-150 focus-within:border-olive-line focus-within:bg-accent',
        fieldClassName,
      )}
    >
      <span className="pointer-events-none absolute inset-y-[13px] left-0 w-[3px] rounded-full bg-olive-icon opacity-0 transition-opacity duration-150 group-focus-within:opacity-100" />
      <span className="flex items-center gap-2.5">
        <span className="col-label text-muted-foreground">{label}</span>
        {counter && <span className="ml-auto text-[11px] text-faint">{counter}</span>}
      </span>
      <textarea
        {...props}
        ref={ref}
        className={cn(
          'w-full resize-y border-0 bg-transparent p-0 text-[15px] font-medium leading-relaxed text-foreground outline-none placeholder:text-faint',
          className,
        )}
      />
    </label>
  ),
);
TextField.displayName = 'TextField';

/**
 * Select encorpado — mesma geometria do `Field`, mas construído em cima do `Select` do
 * shadcn (Radix), não um `<select>` nativo. Nunca troque isso por um select cru: o
 * dropdown precisa vir estilizado (`SelectContent`/`SelectItem`), não a lista do SO.
 */
type FieldSelectOption = { label: string; value: string };

type FieldSelectProps = {
  label: string;
  required?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  options: FieldSelectOption[];
  fieldClassName?: string;
  id?: string;
};

export const FieldSelect: React.FC<FieldSelectProps> = ({
  label,
  required,
  invalid,
  disabled,
  placeholder = 'Selecionar',
  value,
  onChange,
  options,
  fieldClassName,
  id,
}) => (
  <Select
    value={value || undefined}
    // O Radix espelha o valor num <select> nativo escondido e repassa o `change` dele. Quando o
    // valor chega antes das opções (ex: `form.reset` na edição), o nativo não acha a opção, lê ""
    // e isso apagaria o campo. Nenhuma opção tem valor vazio, então "" nunca é escolha do usuário.
    onValueChange={(next) => next !== '' && onChange?.(next)}
    disabled={disabled}
  >
    <SelectTrigger
      id={id}
      className={cn(
        // O SelectTrigger base já vem com `data-[size=default]:h-11` (ver select.tsx) —
        // esse seletor de atributo tem mais especificidade que uma classe comum e vence
        // o h-[66px] normal, então aqui precisa do `!` pra garantir a mesma altura dos
        // outros campos.
        'group relative flex h-[66px]! w-full min-w-0 flex-col items-start justify-center gap-1.5 rounded-[14px] border border-border bg-field px-[18px] py-[13px]',
        'transition-[background-color,border-color] duration-150',
        'data-[state=open]:border-olive-line data-[state=open]:bg-accent',
        '[&>svg]:hidden',
        invalid && 'border-destructive',
        fieldClassName,
      )}
    >
      <span className="pointer-events-none absolute inset-y-[13px] left-0 w-[3px] rounded-full bg-olive-icon opacity-0 transition-opacity duration-150 group-data-[state=open]:opacity-100" />
      <span className="col-label text-muted-foreground">
        {label} {required && <span className="text-sand">*</span>}
      </span>
      <span className="flex w-full items-center gap-2.5">
        <span className="min-w-0 flex-1 truncate text-left text-base font-semibold text-white [&[data-placeholder]]:font-medium [&[data-placeholder]]:text-faint">
          {/* Rótulo passado explícito: sem children o Radix leva o texto do item por portal, e
              ele some quando o valor chega depois da montagem (ex: `form.reset` na edição).
              O `?? null` importa: com `undefined` (campo vazio, ex: cifra nova) o Radix acha
              que não há children e volta a usar o portal, e o tom escolhido fica em branco. */}
          <SelectValue placeholder={placeholder}>
            {options.find((option) => option.value === value)?.label ?? null}
          </SelectValue>
        </span>
        <ChevronDown className="size-4 shrink-0 text-faint" />
      </span>
    </SelectTrigger>
    <SelectContent>
      {options.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/** Busca do topo da listagem: mesma linguagem do Field, porém em uma linha de 56px. */
export const SearchField = forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & { icon: React.ReactNode }
>(({ icon, className, ...props }, ref) => (
  <div className="group relative flex h-14 min-w-0 flex-1 basis-[320px] items-center gap-3 rounded-[14px] border border-border bg-field px-[18px] transition-[background-color,border-color] duration-150 focus-within:border-olive-line focus-within:bg-accent">
    <span className="pointer-events-none absolute inset-y-3 left-0 w-[3px] rounded-full bg-olive-icon opacity-0 transition-opacity duration-150 group-focus-within:opacity-100" />
    <span className="shrink-0 text-olive-icon">{icon}</span>
    <input
      {...props}
      ref={ref}
      className={cn(
        'min-w-0 flex-1 border-0 bg-transparent text-[14.5px] font-medium text-foreground outline-none placeholder:text-faint',
        className,
      )}
    />
  </div>
));
SearchField.displayName = 'SearchField';

/**
 * Select encorpado de filtro (56px): rótulo DENTRO da caixa, valor 14.5px semibold branco, chevron à direita.
 * Mesmo foco do Field (borda olive-line + bg-accent + trilho). Select nativo — funciona bem no celular.
 */
export const SelectField: React.FC<
  React.ComponentProps<'select'> & { label: string; fieldClassName?: string }
> = ({ label, fieldClassName, className, children, ...props }) => (
  <label
    className={cn(
      'group relative flex h-14 min-w-0 cursor-pointer flex-col justify-center gap-[3px] rounded-[14px] border border-border bg-field pl-[18px] pr-[42px]',
      'transition-[background-color,border-color] duration-150 focus-within:border-olive-line focus-within:bg-accent',
      fieldClassName,
    )}
  >
    <span className="pointer-events-none absolute inset-y-3 left-0 w-[3px] rounded-full bg-olive-icon opacity-0 transition-opacity duration-150 group-focus-within:opacity-100" />
    <span className="col-label text-muted-foreground">{label}</span>
    <select
      {...props}
      className={cn(
        'w-full cursor-pointer appearance-none border-0 bg-transparent p-0 text-[14.5px] font-semibold text-white outline-none [&>option]:bg-card',
        className,
      )}
    >
      {children}
    </select>
    <ChevronDown
      aria-hidden
      strokeWidth={1.8}
      className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
    />
  </label>
);
